import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DateTime } from 'luxon';
import { Link as RouterLink } from 'react-router-dom'

import purple from '@material-ui/core/colors/purple';
import green from '@material-ui/core/colors/green';
import yellow from '@material-ui/core/colors/yellow';
import red from '@material-ui/core/colors/red';
import blue from '@material-ui/core/colors/blue';

import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Timeline from '@material-ui/lab/Timeline';
import TimelineItem from '@material-ui/lab/TimelineItem';
import TimelineSeparator from '@material-ui/lab/TimelineSeparator';
import TimelineConnector from '@material-ui/lab/TimelineConnector';
import TimelineContent from '@material-ui/lab/TimelineContent';
import TimelineOppositeContent from '@material-ui/lab/TimelineOppositeContent';
import Typography from '@material-ui/core/Typography';
import Pagination from '@material-ui/lab/Pagination';
import Chip from '@material-ui/core/Chip';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import Link from '@material-ui/core/Link';

import Loading from 'app/components/loaders/Loading';

import useApi from 'app/context/useApi';

import { paper } from 'app/styles/core';

const useStyles = makeStyles(theme => ({
    paper: {
        ...paper(theme),
        position: 'relative',
        marginBottom: theme.spacing(3)
    },
    oppositeContent: {
        flex: 1,
        padding: '4px 16px',
    },
    content: {
        flex: 5,
        padding: '4px 16px 16px 16px',
    },
    contentText: {
        wordBreak: 'break-word'
    },
    marginBottom: {
        marginBottom: theme.spacing(2),
    },
    marginTop: {
        marginTop: theme.spacing(3),
    },
    timeline: {
        minHeight: 700,
    },
    labelNotice: {
        backgroundColor: blue['600'],
        color: '#fff',
        width: 110,
        textAlign: 'center'
    },
    labelWarning: {
        backgroundColor: yellow['900'],
        color: '#fff',
        width: 110,
        textAlign: 'center'
    },
    labelDanger: {
        backgroundColor: red['700'],
        color: '#fff',
        width: 110,
        textAlign: 'center'
    },
    labelCritical: {
        backgroundColor: red['900'],
        color: '#fff',
        width: 110,
        textAlign: 'center'
    },
    adminNotice: {
        backgroundColor: blue['A700'],
        color: '#fff',
        width: 110,
        textAlign: 'center'
    },
    adminWarning: {
        backgroundColor: yellow['A700'],
        color: '#fff',
        width: 110,
        textAlign: 'center'
    },
    adminDanger: {
        backgroundColor: red['A700'],
        color: '#fff',
        width: 110,
        textAlign: 'center'
    },
    adminCritical: {
        backgroundColor: red['A700'],
        color: '#fff',
        width: 110,
        textAlign: 'center'
    },
    labelException: {
        backgroundColor: purple['A700'],
        color: '#fff',
        width: 110,
        textAlign: 'center'
    },
    labelPageview: {
        backgroundColor: green['700'],
        color: '#fff',
        width: 110,
        textAlign: 'center'
    },
    connector: {
        margin: '4px 0'
    },
    emptyTimeline: {
        minHeight: 700,
    },
    marginTopSmall: {
        marginTop: theme.spacing(1)
    }
}))

const AdvancedItem = (props) => {

    const { action, isAdmin } = props;

    const classes = useStyles();

    const admin = action.message.includes('Admin');

    const getLabelClass = () => {
        if (action.type === 'exception') return classes.labelException;
        if (action.type === 'page_view') return classes.labelPageview;

        switch (action.data_c) {
            case 'notice': return admin ? classes.adminNotice : classes.labelNotice;
            case 'warning': return admin ? classes.adminWarning : classes.labelWarning;
            case 'danger':  return admin ? classes.adminDanger : classes.labelDanger;
            case 'critical': return admin ? classes.adminCritical : classes.labelCritical;
        }
    }

    const getLabel = () => {
        if (action.type === 'exception') return 'Exception';
        if (action.type === 'page_view') return 'Page View';

        switch (action.data_c) {
            case 'notice': return admin ? 'Admin Action' : 'Action';
            case 'warning': return admin ? 'Admin Warning' : 'Warning';
            case 'danger': return admin ? 'Admin Danger' : 'Danger';
            case 'critical': return admin ? 'Admin Critical' : 'Critical';
        }
    }

    return (
        <TimelineItem>
            <TimelineOppositeContent className={classes.oppositeContent}>
                <Typography color="textSecondary">
                    {DateTime.fromISO(action.created_at).toLocaleString(DateTime.DATETIME_MED)}
                </Typography>
            </TimelineOppositeContent>
            <TimelineSeparator>
                <Chip label={getLabel()} className={getLabelClass()}/>
                <TimelineConnector className={classes.connector}/>
            </TimelineSeparator>
            <TimelineContent className={classes.content}>
                <Typography className={classes.contentText}>
                    {action.message}
                </Typography>
                {isAdmin ? (
                    <Tooltip title="Admin who performed the action.">
                        <Link
                            color="primary"
                            size="small"
                            component={RouterLink}
                            to={`/admin/users/${action.user.id}`}
                            className={classes.marginTopSmall}
                            >
                            {action.user.full_name}
                        </Link>
                    </Tooltip>
                ) : (action.type === 'action' &&
                    action.data_a !== null &&
                    action.data_a !== "" &&
                    action.data_b !== null &&
                    action.data_b !== ""
                ) ? (
                    <Button
                        variant="text"
                        size="small"
                        className={classes.marginTopSmall}
                        color="primary"
                        component={RouterLink}
                        to={`/admin/${action.data_b}/${action.data_a}/history`}
                        >
                        View Resource
                    </Button>
                ) : null}
            </TimelineContent>
        </TimelineItem>
    )
}

export default function History (props) {

    const { userId } = props;
    const classes = useStyles();
    const api = useApi();

    const [ tab, setTab ] = useState('history');

    const [ history, setHistory ] = useState([]);
    const [ pageCount, setPageCount ] = useState(0);
    const [ page, setPage ] = useState(0);

    const [ adminHistory, setAdminHistory ] = useState([]);
    const [ adminPageCount, setAdminPageCount ] = useState(0);
    const [ adminPage, setAdminPage ] = useState(0);

    const [ loading, setLoading ] = useState(false);
    const [ adminLoading, setAdminLoading ] = useState(false);

    const getHistory = () => {
        setLoading(true);

        api.get(`/admin/user/${userId}/history/${page}`)
            .then(res => {
                setHistory(res.history.data)
                setPageCount(res.history.last_page)
            })
            .finally(() => setLoading(false));
    }

    const getAdminHistory = () => {
        setAdminLoading(true);

        api.get(`/admin/user/${userId}/admin/history/${page}`)
            .then(res => {
                setAdminHistory(res.history.data)
                setAdminPageCount(res.history.last_page)
            })
            .finally(() => setAdminLoading(false));
    }

    useEffect(() => {
        getHistory();
    }, [page]);

    const handlePage = (event, value) => {
        setPage(value);
    }

    useEffect(() => {
        getAdminHistory();
    }, [adminPage]);

    const handleAdminPage = (event, value) => {
        setAdminPage(value);
    }

    const handleTabChange = (event, value) => {
        setTab(value);
    };

    return (
        <div>
            <Paper className={classes.paper}>
                <Grid
                    container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            History
                        </Typography>
                    </Grid>
                </Grid>
                <Typography color="textSecondary">
                    Here you can view a log of a user's actions.
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={loading}/>
                <Tabs
                    onChange={handleTabChange}
                    value={tab}
                    >
                    <Tab value="history" label="User History" />
                    <Tab value="adminHistory" label="Other History" />
                </Tabs>
                {tab === 'history' &&
                    <>
                        {history.length > 0 ? (
                            <Timeline className={classes.timeline}>
                                {history.map(action => <AdvancedItem action={action}/>)}
                            </Timeline>
                        ) : (
                            <Grid container className={classes.emptyTimeline} alignItems="center" justifyContent="center">
                                <Grid item>
                                    <Typography variant="h6" color="textSecondary">
                                        This user has never logged in.
                                    </Typography>
                                </Grid>
                            </Grid>
                        )}
                        <Pagination count={pageCount} showFirstButton showLastButton onChange={handlePage}/>
                    </>
                }
                {tab === 'adminHistory' &&
                    <>
                        {adminHistory.length > 0 ? (
                            <Timeline className={classes.timeline}>
                                {adminHistory.map(action => <AdvancedItem action={action} isAdmin/>)}
                            </Timeline>
                        ) : (
                            <Grid container className={classes.emptyTimeline} alignItems="center" justifyContent="center">
                                <Grid item>
                                    <Typography variant="h6" color="textSecondary">
                                        This user has not been modified by others.
                                    </Typography>
                                </Grid>
                            </Grid>
                        )}
                        <Pagination count={adminPageCount} showFirstButton showLastButton onChange={handleAdminPage}/>
                    </>
                }
            </Paper>
        </div>
    )
}
